import { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../../shared/config/api';
import { productCatalog } from '../data/products';
import { useShop } from '../../../shared/store';
import Skeleton from '../../../shared/components/ui/Skeleton';
const BookFlipViewer = lazy(() => import('../components/BookFlipViewer'));

const BookPageViewSkeleton = () => (
  <div className="flex min-h-screen flex-col items-center justify-center space-y-8 bg-slate-50 p-8">
    <div className="flex w-full max-w-4xl justify-between">
      <Skeleton className="h-10 w-32 rounded-lg" />
      <Skeleton className="h-10 w-48 rounded-lg" />
    </div>
    <div className="flex w-full max-w-5xl justify-center gap-4">
      <Skeleton className="h-[600px] w-[420px] rounded-lg shadow-xl" />
      <Skeleton className="hidden h-[600px] w-[420px] rounded-lg shadow-xl lg:block" />
    </div>
    <div className="flex w-full max-w-md justify-center gap-4">
      <Skeleton variant="circle" className="h-12 w-12" />
      <Skeleton className="h-12 w-32 rounded-full" />
      <Skeleton variant="circle" className="h-12 w-12" />
    </div>
  </div>
);

const BookPageViewPage = () => {
  const { t } = useTranslation();
  const { productId } = useParams();
  const navigate = useNavigate();
  const { products } = useShop();
  const [remoteProduct, setRemoteProduct] = useState<typeof products[number] | null>(null);
  const [isFetchingProduct, setIsFetchingProduct] = useState(false);

  const product = useMemo(
    () => products.find((catalogProduct) => catalogProduct.id === productId),
    [productId, products],
  );
  const effectiveProduct = remoteProduct ?? product;
  const pdfSource = effectiveProduct?.bookUrl || effectiveProduct?.contentUrl || `${API_BASE_URL}/product-files/${productId}`;

  useEffect(() => {
    let cancelled = false;

    const fetchProduct = async () => {
      if (!productId || product?.contentUrl) {
        return;
      }

      setIsFetchingProduct(true);
      try {
        const fetched = productCatalog.find((item) => item.id === productId);
        if (!cancelled && fetched) {
          setRemoteProduct(fetched);
        }
      } catch (error) {
        console.warn('Unable to fetch product by id; using local catalog.', error);
      } finally {
        if (!cancelled) {
          setIsFetchingProduct(false);
        }
      }
    };

    void fetchProduct();
    return () => {
      cancelled = true;
    };
  }, [product, productId]);

  if (!effectiveProduct) {
    if (isFetchingProduct) {
      return <BookPageViewSkeleton />;
    }
    return <Navigate to="/products" replace />;
  }

  return (
    <Suspense fallback={<BookPageViewSkeleton />}>
      <BookFlipViewer
        imageUrl={effectiveProduct.imageUrl}
        title={t(effectiveProduct.nameKey)}
        pages={[]}
        pdfUrl={pdfSource}
        showHeader
        initialViewMode="scroll"
        onBack={() => navigate(`/products/${effectiveProduct.id}`)}
      />
    </Suspense>
  );
};

export default BookPageViewPage;
